import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileText,
  FileUp,
  Globe,
  Hash,
  Mail,
  MapPin,
  PartyPopper,
  Phone,
  Scale,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import z, { type ZodTypeAny } from 'zod';
import type {
  ConditionalVisibility,
  Field,
  FieldShortcut,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { policyControllerPushPolicyEvent } from 'korint-sdk';
import { AssetClaimsModule, InvoicesScheduleModule, QuoteFunnelModule } from '@folio/modules';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { TUTASSUR_RECAP_SHORT_LABELS } from './common';

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const policyHolderInvitationRole = ['DRIVER'];

const showWhenCompanyExists = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'false',
  },
] as const satisfies ConditionalVisibility[];

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const financialCompanySections: FormSectionsWithShortcuts = [
  {
    title: "Informations financières de l'année dernière ou projection",
    fields: [{ name: 'companyRevenue', icon: Euro }],
  },
];

const companyFunnelSections = [
  {
    title: "Informations de l'entreprise",
    fields: [
      {
        name: 'isCompanyBeingCreated',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.isCompanyBeingCreated ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isCompanyBeingCreated === 'true',
      },
      {
        name: 'companySiret',
        required: false,
        type: 'company',
        displayCompanyInformation: true,
        icon: Search,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                  city: apiData?.customFields?.companyCity,
                  address: apiData?.customFields?.companyAddress,
                },
                nafCode: apiData?.customFields?.companyNafCode,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        isCustomField: true,
        showWhen: showWhenCompanyExists,
      },
      {
        name: 'companyName',
        required: true,
        type: 'text',
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        colSpan: 4 as const,
        icon: Hash,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCity',
        required: true,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyLegalStatus',
        required: true,
        type: 'text',
        icon: FileText,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        required: true,
        type: 'date',
        colSpan: 6 as const,
        icon: CalendarDays,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyNafCode',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: Hash,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyStaffCount',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        icon: Users,
        isCustomField: true,
      },
      {
        name: 'companyActivity',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: BriefcaseIcon,
        isCustomField: true,
      },
    ],
  },
  ...financialCompanySections,
] as const satisfies FormSectionsWithShortcuts;

const contactFunnelSections = [
  {
    title: 'Informations du contact',
    fields: [
      {
        name: 'contactCivility',
        required: true,
        type: 'select',
        display: 'small-cards' as const,
        options: [
          { value: 'MONSIEUR', label: 'Monsieur' },
          { value: 'MADAME', label: 'Madame' },
          { value: 'MADEMOISELLE', label: 'Mademoiselle' },
        ],
        icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User },
        isCustomField: true,
      },
      {
        name: 'contactFirstName',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: User,
        isCustomField: true,
      },
      {
        name: 'contactLastName',
        required: true,
        type: 'text',
        colSpan: 6 as const,
        icon: User,
        isCustomField: true,
      },
      {
        name: 'contactEmail',
        required: true,
        type: 'email',
        colSpan: 6 as const,
        icon: Mail,
        isCustomField: true,
      },
      {
        name: 'contactPhone',
        required: true,
        type: 'phoneNumber',
        countries: ['FR'],
        colSpan: 6 as const,
        icon: Phone,
        isCustomField: true,
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const previousInsurerSections = [
  {
    title: "Historique d'assurance",
    fields: [
      {
        name: 'hasTheCompanyBeenInsuredInTheLast36Months',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldCheck,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.hasTheCompanyBeenInsuredInTheLast36Months ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasTheCompanyBeenInsuredInTheLast36Months === 'true',
      },
      {
        name: 'previousInsuranceCompanyName',
        required: true,
        type: 'text',
        icon: Building2,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'previousInsuranceContractNumber',
        required: false,
        type: 'text',
        icon: Hash,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'companyCancellation',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: XCircle,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.companyCancellation ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.companyCancellation === 'true',
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'cancellationDate',
        required: false,
        type: 'date',
        icon: CalendarDays,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
          {
            fieldName: 'companyCancellation',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
      {
        name: 'cancellationReason',
        required: true,
        type: 'select',
        options: [
          { value: 'OTHER', label: 'Autre' },
          { value: 'JUDICIAL_LIQUIDATION', label: 'Liquidation judiciaire' },
          { value: 'NON_PAYMENT', label: 'Non-paiement' },
          { value: 'JUDICIAL_RECOVERY', label: 'Redressement judiciaire' },
          { value: 'DISASTERS', label: 'Sinistres' },
        ],
        icons: {
          OTHER: XCircle,
          JUDICIAL_LIQUIDATION: Scale,
          NON_PAYMENT: Euro,
          JUDICIAL_RECOVERY: Scale,
          DISASTERS: AlertTriangle,
        },
        perRow: 3,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'hasTheCompanyBeenInsuredInTheLast36Months',
            condition: 'equals',
            value: 'true',
          },
          {
            fieldName: 'companyCancellation',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const quoteAssetFields: FieldShortcut[] = [
  {
    name: 'startedAt',
    required: true,
    type: 'datetime',
    min: 'now',
    icon: CalendarDays,
  },
];

const nonDelegatedQuotePolicySections = [
  {
    title: 'Options de garantie',
    fields: [
      {
        name: 'chosenProfessionalCivilLiability',
        required: true,
        type: 'select',
        icon: Shield,
        options: [
          { value: 'FIFTY_THOUSAND', label: '50 000 €' },
          { value: 'ONE_HUNDRED_THOUSAND', label: '100 000 €' },
          { value: 'ONE_HUNDRED_FIFTY_THOUSAND', label: '150 000 €' },
          { value: 'TWO_HUNDRED_THOUSAND', label: '200 000 €' },
          { value: 'THREE_HUNDRED_THOUSAND', label: '300 000 €' },
          { value: 'FIVE_HUNDRED_THOUSAND', label: '500 000 €' },
          { value: 'SEVEN_HUNDRED_FIFTY_THOUSAND', label: '750 000 €' },
          { value: 'ONE_MILLION', label: '1 000 000 €' },
          { value: 'OVER_ONE_MILLION', label: '+ de 1 000 000 €' },
        ],
        isCustomField: true,
      },
      {
        name: 'chosenExcess',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        isCustomField: true,
      },
      {
        name: 'chosenLegalProtection',
        required: true,
        type: 'select',
        options: [
          { value: 'NONE', label: 'Aucune' },
          { value: 'BASIC_FORMULA', label: 'Protection juridique essentielle' },
          { value: 'EXTENDED_FORMULA', label: 'Protection juridique summum' },
        ],
        icons: {
          NONE: XCircle,
          BASIC_FORMULA: Shield,
          EXTENDED_FORMULA: ShieldCheck,
        },
        isCustomField: true,
      },
      {
        name: 'chosenExploitationCivilLiability',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Shield,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.chosenExploitationCivilLiability ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.chosenExploitationCivilLiability === 'true',
      },
      {
        name: 'chosenDirectorsLiability',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Users,
        isCustomField: true,
        apiDataAccessor: (apiData: { customFields?: Record<string, unknown> }) =>
          apiData.customFields?.chosenDirectorsLiability ? 'true' : 'false',
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.chosenDirectorsLiability === 'true',
      },
      {
        name: 'chosenMobilityOption',
        type: 'radio',
        display: 'toggle' as const,
        required: true,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: BriefcaseIcon,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.chosenMobilityOption ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.chosenMobilityOption === 'true',
      },
      {
        name: 'chosenMobilityAmount',
        required: false,
        type: 'number',
        icon: Euro,
        isCustomField: true,
        showWhen: [
          {
            fieldName: 'chosenMobilityOption',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
    ] as const satisfies Array<Field>,
  },
] as const satisfies FormSectionsWithShortcuts;

const KILLING_MESSAGE = 'Il est impossible de souscrire une police dans ce cas';

const killingIfFalse = {
  blockingValue: 'false',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(z.literal('true', { errorMap: () => ({ message: KILLING_MESSAGE }) })),
};

const killingIfTrue = {
  blockingValue: 'true',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(z.literal('false', { errorMap: () => ({ message: KILLING_MESSAGE }) })),
};

const additionalQuestionsSections = [
  {
    fields: [
      {
        name: 'hasReligiousPoliticalUnionActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Users,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasReligiousPoliticalUnionActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasReligiousPoliticalUnionActivity === 'true',
      },
      {
        name: 'hasHighRiskActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: AlertTriangle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasHighRiskActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasHighRiskActivity === 'true',
      },
      {
        name: 'hasDecennialLiabilityActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasDecennialLiabilityActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasDecennialLiabilityActivity === 'true',
      },
      {
        name: 'isMedicalActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: ShieldAlert,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isMedicalActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isMedicalActivity === 'true',
      },
      {
        name: 'isLegalActivity',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Scale,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isLegalActivity ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isLegalActivity === 'true',
      },
      {
        name: 'hasBeenTerminatedByTutassur',
        ...killingIfTrue,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: XCircle,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.hasBeenTerminatedByTutassur ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.hasBeenTerminatedByTutassur === 'true',
      },
    ],
  },
  {
    fields: [
      {
        name: 'isInMetropolitanFrance',
        ...killingIfFalse,
        required: true,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Globe,
        isCustomField: true,
        apiDataAccessor: apiData => {
          return apiData.customFields?.isInMetropolitanFrance ? 'true' : 'false';
        },
        formDataAccessor: (formData: Record<string, unknown>) =>
          formData.isInMetropolitanFrance === 'true',
      },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const productCustomPath = '/responsabilite-civile-pro-sur-mesure';

const manualComponentsGroups: Array<{
  id: string;
  componentIds: string[];
  placeholders?: Record<string, string>;
}> = [
  {
    id: 'premium',
    componentIds: ['PREMIUM', 'WHOLESALE_COMMISSION', 'INSURANCE_TAX'],
    placeholders: {
      PREMIUM: '10 000',
      WHOLESALE_COMMISSION: '1 500',
      INSURANCE_TAX: '350',
    },
  },
  {
    id: 'wholesaleFees',
    componentIds: ['WHOLESALE_ADDITIONAL_COMMISSION', 'WHOLESALE_SUBSCRIPTION_FEE', 'RENEWAL_FEE'],
    placeholders: {
      WHOLESALE_ADDITIONAL_COMMISSION: '5',
      WHOLESALE_SUBSCRIPTION_FEE: '150',
      RENEWAL_FEE: '80',
    },
  },
  {
    id: 'retailFees',
    componentIds: ['RETAIL_COMMISSION', 'RETAIL_SUBSCRIPTION_FEE', 'RETAIL_MANAGEMENT_FEE'],
    placeholders: {
      RETAIL_COMMISSION: '10',
      RETAIL_SUBSCRIPTION_FEE: '100',
      RETAIL_MANAGEMENT_FEE: '200',
    },
  },
];

export const tutassurMilaResponsabiliteCivileProConfig = {
  display: {
    category: 'professionnel',
    icon: BriefcaseIcon,
    badges: ['custom'],
  },
  productCustomPath,
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: companyFunnelSections,
      },
      {
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        icon: User,
        sections: contactFunnelSections,
      },
      {
        path: 'previous-insurer',
        type: 'customerFunnel',
        icon: ShieldCheck,
        sections: previousInsurerSections,
      },
      {
        path: 'claims',
        type: 'module',
        icon: AlertTriangle,
        module: <AssetClaimsModule />,
      },
      {
        path: 'additional-questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: additionalQuestionsSections,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={quoteAssetFields}
            policySections={nonDelegatedQuotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
        additionalComponents: {
          isQuoteTierModuleVisible: false,
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: Building2,
        sections: companyFunnelSections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'contact',
        type: 'customerFunnel',
        icon: User,
        sections: contactFunnelSections,
      },
      {
        path: 'previous-insurer',
        type: 'customerFunnel',
        icon: ShieldCheck,
        sections: previousInsurerSections,
      },
      {
        path: 'claims',
        type: 'module',
        icon: AlertTriangle,
        module: <AssetClaimsModule />,
      },
      {
        path: 'additional-questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: additionalQuestionsSections,
      },
      {
        path: 'quote',
        type: 'custom',
        icon: Shield,
        component: (
          <QuoteFunnelModule
            assetFields={quoteAssetFields}
            policySections={nonDelegatedQuotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        callback: {},
        additionalComponents: {
          isQuoteTierModuleVisible: true,
        },
      },
    ],
  },
  policyholderRenewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: financialCompanySections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId }) => `/policyholder/${policyId}/information`,
      },
    ],
  },
  renewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: financialCompanySections,
      },
      {
        path: 'quote',
        type: 'custom',
        component: (
          <QuoteFunnelModule
            policySections={nonDelegatedQuotePolicySections}
            nonDelegatedDocuments={['QUOTE']}
            manualComponentsGroups={manualComponentsGroups}
            recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
          />
        ),
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },

        callback: {},
        redirectTo: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}`,
      },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      {
        key: 'companyName',
        type: 'text',
        contactKey: ContactKey.COMPANY_NAME,
      },
      {
        key: 'companyAddress',
        type: 'text',
      },
      {
        key: 'companyHeadquarterPostcode',
        type: 'postcode',
        countries: ['FR'],
      },
      {
        key: 'companyCity',
        type: 'text',
      },
      {
        key: 'companySiret',
        type: 'text',
      },
      {
        key: 'companyNafCode',
        type: 'text',
      },
      {
        key: 'companyRevenue',
        type: 'number',
      },
      {
        key: 'companyStaffCount',
        type: 'number',
      },
      {
        key: 'companyActivity',
        type: 'text',
      },
      {
        key: 'isCompanyBeingCreated',
        type: 'boolean',
      },
      {
        key: 'contactFirstName',
        type: 'text',
        contactKey: ContactKey.FIRST_NAME,
      },
      {
        key: 'contactLastName',
        type: 'text',
        contactKey: ContactKey.LAST_NAME,
      },
      {
        key: 'contactEmail',
        type: 'text',
      },
      {
        key: 'contactPhone',
        type: 'text',
      },
      {
        key: 'contactCivility',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const civilityMap: Record<string, string> = {
            MONSIEUR: 'Monsieur',
            MADAME: 'Madame',
            MADEMOISELLE: 'Mademoiselle',
          };
          return civilityMap[value] ?? value;
        },
      },
      {
        key: 'contactPostalCode',
        type: 'text',
      },
      {
        key: 'contactCity',
        type: 'text',
      },
      {
        key: 'contactCountry',
        type: 'text',
      },
    ],
  },
  policyInformationCard: {
    title: 'Informations de la police',
    fieldsConfig: [
      {
        key: 'allClientDocumentsOk',
        type: 'text',
      },
      {
        key: 'chosenExcess',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          return `${value} €`;
        },
      },
      {
        key: 'chosenLegalProtection',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const legalProtectionMap: Record<string, string> = {
            NONE: 'Aucune',
            BASIC_FORMULA: 'Protection juridique essentielle',
            EXTENDED_FORMULA: 'Protection juridique summum',
          };
          return legalProtectionMap[value] ?? value;
        },
      },
      {
        key: 'chosenProfessionalCivilLiability',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const professionalCivilLiabilityMap: Record<string, string> = {
            FIFTY_THOUSAND: '50 000 €',
            ONE_HUNDRED_THOUSAND: '100 000 €',
            ONE_HUNDRED_FIFTY_THOUSAND: '150 000 €',
            TWO_HUNDRED_THOUSAND: '200 000 €',
            THREE_HUNDRED_THOUSAND: '300 000 €',
            FIVE_HUNDRED_THOUSAND: '500 000 €',
            SEVEN_HUNDRED_FIFTY_THOUSAND: '750 000 €',
            ONE_MILLION: '1 000 000 €',
            OVER_ONE_MILLION: '+ de 1 000 000 €',
          };
          return professionalCivilLiabilityMap[value] ?? value;
        },
      },
      {
        key: 'chosenExploitationCivilLiability',
        type: 'boolean',
      },
      {
        key: 'chosenMobility',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const mobilityAmountMap: Record<string, string> = {
            NONE: 'Aucune',
            FIVE_THOUSAND: '5 000 €',
            TEN_THOUSAND: '10 000 €',
          };
          return mobilityAmountMap[value] ?? value;
        },
      },
      {
        key: 'chosenExploitationLoss',
        type: 'text',
        formatValue: (value: string | number | boolean | undefined) => {
          if (typeof value !== 'string') return undefined;
          const exploitationLossDurationMap: Record<string, string> = {
            NONE: 'Aucune',
            TWELVE_MONTHS: '12 mois',
            EIGHTEEN_MONTHS: '18 mois',
            TWENTY_FOUR_MONTHS: '24 mois',
          };
          return exploitationLossDurationMap[value] ?? value;
        },
      },
      {
        key: 'chosenDirectorsLiability',
        type: 'boolean',
      },
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery${productCustomPath}/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
    customModules: [],
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'TOTAL_PREMIUM', variant: 'dimmed' }] },
      { lines: [{ key: 'BROKERAGE_FEES' }] },
      { lines: [{ key: 'MANAGEMENT_FEE' }] },
      {
        lines: [{ key: 'SUBSCRIPTION_FEE' }, { key: 'RENEWAL_FEE', tooltip: true }],
      },
    ],
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-supporting-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'wholesaler-quote-uploaded',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ documents }) =>
          documents.some(({ type, category }) => type === 'non_delegated' && category === 'QUOTE'),
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: true,
    enablePolicyComparison: true,
    quoteDerogationFunnelModule: {
      afterEndPath: '/derogations',
      steps: [
        {
          path: 'quote',
          type: 'custom',
          component: (
            <QuoteFunnelModule
              nonDelegatedDocuments={['QUOTE']}
              assetFields={quoteAssetFields}
              policySections={nonDelegatedQuotePolicySections}
              manualComponentsGroups={manualComponentsGroups}
              recapLabelOverrides={TUTASSUR_RECAP_SHORT_LABELS}
            />
          ),
        },
      ],
    },
  },
  claims: {
    claimsCategories: [
      {
        category: 'FIRE_AND_EXPLOSION',
        label: 'Incendies, explosions et événements assimilés',
      },
      {
        category: 'ELECTRICAL_DAMAGE',
        label: 'Dommages électriques',
      },
      {
        category: 'STORED_GOODS',
        label: 'Marchandises conservées',
      },
      {
        category: 'WATER_DAMAGE',
        label: 'Dégâts des eaux',
      },
      {
        category: 'THEFT_VANDALISM_ATTEMPTED_THEFT',
        label: 'Vol, vandalisme et tentative de vol',
      },
      {
        category: 'CLIMATIC_EVENTS',
        label: 'Évènements climatiques',
      },
      {
        category: 'NATURAL_DISASTERS',
        label: 'Catastrophes Naturelles',
      },
      {
        category: 'TERRORIST_ATTACKS',
        label: 'Attentats',
      },
      {
        category: 'ELECTRICAL_ELECTRONIC_EQUIPMENT_BREAKDOWN',
        label: 'Bris de matériel électrique et/ou électronique',
      },
      {
        category: 'LOSS_OF_GOODS_IN_CONTROLLED_ATMOSPHERE',
        label: 'Pertes de marchandises conservées en atmosphère contrôlée',
      },
      {
        category: 'EXPENSES_AND_LOSSES',
        label: 'Frais et pertes',
      },
      {
        category: 'PROFESSIONAL_PREMISES_LIABILITY',
        label:
          'Responsabilité du fait de vos locaux professionnels et de vos aménagements extérieurs',
      },
    ],
    fieldsConfig: [
      {
        name: 'amount',
        type: 'number',
        required: true,
        isCustomField: true,
      },
    ],
  },
} satisfies KorintModulesProductsConfig;
